// User roles management for the application
import { db, auth } from './firebase';
import { doc, getDoc, setDoc, collection, query, where, getDocs, arrayUnion, arrayRemove, deleteDoc } from 'firebase/firestore';

// Define roles and their hierarchy
export const ROLES = {
  ADMIN: 'admin',       // Can do everything, including user management
  GESTOR: 'gestor',     // Can view all actas but cannot manage users
  APROBADOR: 'aprobador', // Can approve actas but only for assigned projects
  USUARIO: 'usuario'    // Default role, can only view/create their own actas
};

// Check if a user has a specific role or higher
export const hasRole = (userRole, requiredRole) => {
  if (!userRole) return false;
  
  // Role hierarchy
  const roleHierarchy = {
    [ROLES.ADMIN]: 4,
    [ROLES.GESTOR]: 3,
    [ROLES.APROBADOR]: 2,
    [ROLES.USUARIO]: 1
  };
  
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
};

// Get user role from Firestore
export const getUserRole = async (userId) => {
  try {
    console.log(`🔍 getUserRole llamado para userId: ${userId}`);
    const userRef = doc(db, 'useractas', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      console.log(`✅ Usuario encontrado con userId ${userId}, rol: ${userSnap.data().role}`);
      return userSnap.data().role;
    } else {
      console.log(`❌ Usuario con userId ${userId} no existe. Verificando por email...`);
      // If user doesn't exist in the collection, check if there's already a user with the same email
      const user = auth.currentUser;
      if (user) {
        console.log(`📧 Buscando usuario existente con email: ${user.email}`);
        // First, check if a user with this email already exists
        const usersRef = collection(db, 'useractas');
        const emailQuery = query(usersRef, where('email', '==', user.email));
        const emailQuerySnapshot = await getDocs(emailQuery);
        
        console.log(`📊 Encontrados ${emailQuerySnapshot.size} usuarios con email ${user.email}`);
        
        if (!emailQuerySnapshot.empty) {
          // User with this email already exists, update the existing record with the new userId
          const existingUserDoc = emailQuerySnapshot.docs[0];
          const existingUserData = existingUserDoc.data();
          
          console.log(`👤 Usuario con email ${user.email} ya existe con ID: ${existingUserDoc.id}`);
          console.log(`📋 Datos existentes:`, existingUserData);
          
          // Delete the old document and create a new one with the correct userId
          await setDoc(userRef, {
            ...existingUserData,
            updatedAt: new Date()
          });
          console.log(`✅ Nuevo documento creado con userId: ${userId}`);
          
          // Delete the old document if it has a different ID
          if (existingUserDoc.id !== userId) {
            await deleteDoc(doc(db, 'useractas', existingUserDoc.id));
            console.log(`🗑️ Documento duplicado eliminado: ${existingUserDoc.id}`);
          }
          
          console.log(`🎭 Retornando rol: ${existingUserData.role}`);
          return existingUserData.role;
        } else {
          console.log(`🆕 No existe usuario con email ${user.email}. Creando nuevo usuario...`);
          // No user with this email exists, create a new one
          await setDoc(userRef, {
            email: user.email,
            role: ROLES.USUARIO, // Default role for new users
            createdAt: new Date()
          });
          console.log(`✅ Nuevo usuario creado con rol USUARIO para: ${user.email}`);
          return ROLES.USUARIO;
        }
      } else {
        console.log(`❌ No hay usuario autenticado actualmente`);
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
};

// Set user role (admin only function)
export const setUserRole = async (userId, newRole) => {
  try {
    const userRef = doc(db, 'useractas', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      // Update existing user
      await setDoc(userRef, { role: newRole }, { merge: true });
    } else {
      // Get user email if possible
      const usersQuery = query(collection(db, 'useractas'), where('userId', '==', userId));
      const querySnapshot = await getDocs(usersQuery);
      let email = '';
      
      if (!querySnapshot.empty) {
        email = querySnapshot.docs[0].data().email;
      }
      
      // Create new user with role
      await setDoc(userRef, {
        email,
        role: newRole,
        updatedAt: new Date()
      });
    }
    return true;
  } catch (error) {
    console.error('Error setting user role:', error);
    return false;
  }
};

// Get all users with their roles (admin only function)
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'useractas');
    const querySnapshot = await getDocs(usersRef);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data(),
        // Asegurar que allowedProjects siempre exista
        allowedProjects: doc.data().allowedProjects || [],
        // Asegurar que allowedContractProjects siempre exista
        allowedContractProjects: doc.data().allowedContractProjects || []
      });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting all users:', error);
    return [];
  }
};

// Asignar permisos de proyecto a un usuario (mantener compatibilidad)
export const assignProjectToUser = async (userId, projectName) => {
  try {
    const userRef = doc(db, 'useractas', userId);
    await setDoc(userRef, {
      allowedProjects: arrayUnion(projectName),
      updatedAt: new Date()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error asignando proyecto al usuario:', error);
    return false;
  }
};

// Remover permisos de proyecto de un usuario (mantener compatibilidad)
export const removeProjectFromUser = async (userId, projectName) => {
  try {
    const userRef = doc(db, 'useractas', userId);
    await setDoc(userRef, {
      allowedProjects: arrayRemove(projectName),
      updatedAt: new Date()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error removiendo proyecto del usuario:', error);
    return false;
  }
};

// Asignar permisos de combinación contrato/proyecto a un usuario
export const assignContractProjectToUser = async (userId, contractProject) => {
  try {
    const userRef = doc(db, 'useractas', userId);
    await setDoc(userRef, {
      allowedContractProjects: arrayUnion(contractProject),
      updatedAt: new Date()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error asignando combinación contrato/proyecto al usuario:', error);
    return false;
  }
};

// Remover permisos de combinación contrato/proyecto de un usuario
export const removeContractProjectFromUser = async (userId, contractProject) => {
  try {
    const userRef = doc(db, 'useractas', userId);
    await setDoc(userRef, {
      allowedContractProjects: arrayRemove(contractProject),
      updatedAt: new Date()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error('Error removiendo combinación contrato/proyecto del usuario:', error);
    return false;
  }
};

// Verificar si un usuario tiene permiso para ver un acta específica
export const canUserViewActa = (userRole, userId, acta, userAllowedProjects) => {
  // Administradores y gestores pueden ver todas las actas
  if (hasRole(userRole, ROLES.GESTOR)) {
    return true;
  }
  
  // El creador del acta siempre puede verla
  if (acta.creatorId === userId) {
    return true;
  }
  
  // Verificar si el usuario tiene permiso para el proyecto del acta
  if (userAllowedProjects && userAllowedProjects.includes(acta.tipoProyecto)) {
    return true;
  }
  
  // Por defecto, no tiene permiso
  return false;
};

// Obtener los proyectos permitidos para un usuario
export const getUserAllowedProjects = async (userId) => {
  try {
    const userRef = doc(db, 'useractas', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data().allowedProjects || [];
    }
    return [];
  } catch (error) {
    console.error('Error obteniendo proyectos permitidos:', error);
    return [];
  }
};

// Obtener las combinaciones contrato/proyecto permitidas para un usuario
export const getUserAllowedContractProjects = async (userId) => {
  try {
    const userRef = doc(db, 'useractas', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data().allowedContractProjects || [];
    }
    return [];
  } catch (error) {
    console.error('Error obteniendo combinaciones contrato/proyecto permitidas:', error);
    return [];
  }
};

// Generar clave única para combinación contrato/proyecto
export const generateContractProjectKey = (contractName, projectName) => {
  return `${contractName}|${projectName}`;
};

// Obtener todos los datos de un usuario
export const getUserData = async (userId) => {
  try {
    const userRef = doc(db, 'useractas', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return {
        ...userSnap.data(),
        allowedProjects: userSnap.data().allowedProjects || [],
        allowedContractProjects: userSnap.data().allowedContractProjects || []
      };
    }
    return {
      allowedProjects: [],
      allowedContractProjects: []
    };
  } catch (error) {
    console.error('Error obteniendo datos del usuario:', error);
    return {
      allowedProjects: [],
      allowedContractProjects: []
    };
  }
};

// Verificar si un usuario tiene permiso para una combinación contrato/proyecto específica
export const canUserAccessContractProject = (userRole, userAllowedContractProjects, contractName, projectName) => {
  // Administradores y gestores pueden acceder a todas las combinaciones
  if (hasRole(userRole, ROLES.GESTOR)) {
    return true;
  }
  
  // Verificar si el usuario tiene permiso para esta combinación específica
  const contractProjectKey = generateContractProjectKey(contractName, projectName);
  return userAllowedContractProjects && userAllowedContractProjects.includes(contractProjectKey);
};